import React, { useMemo, useEffect, useRef } from 'react';
import { markdown as MarkdownService } from 'markdown';
import { useSelector, useDispatch } from 'react-redux';
import { openDrawer } from 'Services/Drawer';
import { RootState } from 'Store/index';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import { openEditOrder } from 'App/components/EditOrder';

type Props = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
	markdown: string;
	linkTarget?: '_blank' | '_parent' | '_self' | '_top';
	compact?: boolean;
	enableEntityLinks?: boolean;
};

const Markdown = (props: Props) => {
	const { markdown, linkTarget, compact, enableEntityLinks = false, ...divProps } = props;
	const containerRef = useRef<HTMLDivElement>(null);
	const dispatch = useDispatch();

	const customerId = useSelector((state: RootState) => state.App?.customerId);

	const handleEntityClick = (entityType: string, id: number) => {
		switch (entityType) {
			case 'todo':
				openDrawer('EditTodo', { todo: { id } });
				break;
			case 'appointment':
				Tools.$upModal.open('editAppointment', { id });
				break;
			case 'phonecall':
				openDrawer('EditPhonecall', { activity: { id } });
				break;
			case 'order':
			case 'opportunity':
				openEditOrder({ id });
				break;
			case 'activity':
				Tools.$upModal.open('editActivity', { id });
				break;
			case 'agreement':
				openAgreement({ agreementId: id });
				break;
			default:
				return false;
		}
		return true;
	};

	const HTML = useMemo(() => {
		let HTML = MarkdownService.toHTML(markdown);

		// Convert entity links (/client/{id}, /contact/{id}, /todo/{id}, etc.) to proper URLs
		// Client and contact open in new tabs, other entities get data attributes for click handling
		if (enableEntityLinks) {
			HTML = HTML.replace(
				/<a\s+[^>]*href=["']\/(client|contact|todo|order|opportunity|activity|appointment|phonecall|agreement)\/(\d+)["'][^>]*>([\s\S]*?)<\/a>/g,
				(match, entityType, id, linkText) => {
					const parsedId = parseInt(id);
					if (isNaN(parsedId)) {
						return match;
					}
					const attrMatch = match.match(/<a\s+([^>]*)>/);
					const allAttrs = attrMatch ? attrMatch[1] : '';
					const otherAttrs = allAttrs.replace(/href=["'][^"']*["']/g, '').trim();
					const attrsStr = otherAttrs ? ` ${otherAttrs}` : '';

					// Client and contact open in new tabs, other entities get data attributes for click handling
					if (entityType === 'client' || entityType === 'contact') {
						const stateName = entityType === 'client' ? 'account.dashboard' : 'contact.dashboard';
						let href = Tools.$state?.href?.(stateName, { id: parseInt(id), customerId });

						if (href) {
							// Normalize href to start with /#/ if it contains a hash
							// This prevents relative URLs from including the current path
							const hashIndex = href.indexOf('#');
							if (hashIndex !== -1) {
								const hashPart = href.substring(hashIndex);
								href = hashPart.startsWith('#/') ? `/${hashPart}` : `/#${hashPart.substring(1)}`;
							} else {
								// If no hash, ensure it's an absolute path from root
								href = href.startsWith('/') ? href : `/${href}`;
							}

							const cleanAttrs = attrsStr.replace(/\s*target=["'][^"']*["']/g, '');
							return `<a href="${href}"${cleanAttrs} target="_blank" data-entity-type="${entityType}" data-entity-id="${parsedId}">${linkText}</a>`;
						}
						return match;
					}

					return `<a href="/${entityType}/${parsedId}"${attrsStr} data-entity-type="${entityType}" data-entity-id="${parsedId}">${linkText}</a>`;
				}
			);
		}

		if (linkTarget) {
			HTML = HTML.replace(/<a/g, `<a target="${linkTarget}"`);
		}

		// Removes hr tags and extra newlines that the AI-chat can generate
		if (compact) {
			HTML = HTML.replace(/<\/p>\s*<p>/g, '</p><p>')
				.replace(/<\/blockquote>\s*<p>/g, '</blockquote><p>')
				.replace(/<\/ul>\s*<p>/g, '</ul><p>')
				.replace(/<\/ol>\s*<p>/g, '</ol><p>')
				.replace(/<hr\s*\/?>/g, '')
				.replace(/\n\s*\n/g, '\n')
				.replace(/>\s+</g, '><');
		}

		return HTML;
	}, [markdown, linkTarget, compact, enableEntityLinks, customerId]);

	useEffect(() => {
		if (!enableEntityLinks || !containerRef.current) return;

		const handleEntityLinkClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const link = target.closest('a');

			if (!link) return;

			const entityType = link.getAttribute('data-entity-type');
			const entityId = link.getAttribute('data-entity-id');

			if (!entityType || !entityId) return;

			const id = parseInt(entityId, 10);
			if (isNaN(id)) return;

			// Client and contact open in new tabs, don't intercept
			if (entityType === 'client' || entityType === 'contact') {
				return;
			}

			const handled = handleEntityClick(entityType, id);
			if (handled) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		const container = containerRef.current;
		container.addEventListener('click', handleEntityLinkClick);

		return () => {
			container.removeEventListener('click', handleEntityLinkClick);
		};
	}, [enableEntityLinks, dispatch]);

	return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: HTML }} {...divProps} />;
};

export default Markdown;
